import { useCallback, useEffect, useRef } from 'react'
import { useFormContext, UseFormReturn } from 'react-hook-form'

import {
  InvitePermission,
  InvitePermissionOption,
  InvitePersonalAccount
} from '@/api/models/users/lib/invites.types'
import { AppCheckbox } from '@/components/shared/checkbox'

import {
  InviteCreationFormData,
  SelectedPersonalAccount
} from '../model/schemas'
import { PremisePicker } from './PremisePicker'

type Props = {
  permissions: InvitePermission[]
}

const buildSelectedAccount = (
  account: InvitePersonalAccount,
  project: Pick<InvitePermission, 'projectId' | 'projectName'>
): SelectedPersonalAccount => ({
  personalAccountId: account.id,
  permissions: account.permissions.manualSelection,
  autoPermissions: account.permissions.autoSelection,
  projectId: project.projectId,
  projectName: project.projectName,
  premises: account.premises.map(premise => ({
    premiseId: premise.id,
    premiseName: premise.name
  }))
})

export const PermissionsSelector = ({ permissions }: Props) => {
  const {
    setValue,
    clearErrors,
    watch,
    formState: { errors }
  }: UseFormReturn<InviteCreationFormData> = useFormContext()

  const hasAutoSelected = useRef(false)
  const selectedAccounts = watch('personalAccounts')
  const hasError = Boolean(errors.personalAccounts?.message)

  const isAccountSelected = (accountId: string) =>
    selectedAccounts.some(account => account.personalAccountId === accountId)

  const updateAccounts = useCallback(
    (
      updater: (prev: SelectedPersonalAccount[]) => SelectedPersonalAccount[]
    ) => {
      setValue('personalAccounts', updater(selectedAccounts), {
        shouldValidate: false
      })
      clearErrors('personalAccounts')
    },
    [clearErrors, selectedAccounts, setValue]
  )

  const onProjectToggle = useCallback(
    (project: InvitePermission) => {
      const projectAccountIds = new Set(
        project.personalAccounts.map(account => account.id)
      )

      const isAnyAccountSelected = selectedAccounts.some(selected =>
        projectAccountIds.has(selected.personalAccountId)
      )

      if (isAnyAccountSelected) {
        updateAccounts(prevSelectedAccounts =>
          prevSelectedAccounts.filter(
            selectedAccount =>
              !projectAccountIds.has(selectedAccount.personalAccountId)
          )
        )
        return
      }

      const newAccounts = project.personalAccounts.map(account =>
        buildSelectedAccount(account, project)
      )
      updateAccounts(prev => [...prev, ...newAccounts])
    },
    [selectedAccounts, updateAccounts]
  )

  const onAccountSelect = useCallback(
    (
      account: InvitePersonalAccount,
      project: Pick<InvitePermission, 'projectId' | 'projectName'>
    ) => {
      const isAlreadySelected = selectedAccounts.some(
        selected => selected.personalAccountId === account.id
      )

      if (isAlreadySelected) {
        updateAccounts(prev =>
          prev.filter(selected => selected.personalAccountId !== account.id)
        )
        return
      }

      updateAccounts(prev => [...prev, buildSelectedAccount(account, project)])
    },
    [selectedAccounts, updateAccounts]
  )

  const onAccountPermissionsChange = useCallback(
    (accountId: string, nextPermissions: InvitePermissionOption[]) => {
      const autoPermissions =
        selectedAccounts.find(
          selectedAccount => selectedAccount.personalAccountId === accountId
        )?.autoPermissions ?? []
      const manualPermissions = nextPermissions.filter(
        permission =>
          !autoPermissions.some(
            autoPermission => autoPermission.alias === permission.alias
          )
      )

      if (nextPermissions.length === 0) {
        updateAccounts(prev =>
          prev.filter(account => account.personalAccountId !== accountId)
        )
        return
      }

      updateAccounts(prev =>
        prev.map(account =>
          account.personalAccountId === accountId
            ? { ...account, permissions: manualPermissions }
            : account
        )
      )
    },
    [selectedAccounts, updateAccounts]
  )

  useEffect(() => {
    if (!hasAutoSelected.current && permissions.length === 1) {
      hasAutoSelected.current = true
      onProjectToggle(permissions[0])
    }
  }, [onProjectToggle, permissions])

  const getAccountPermissions = (accountId: string) => {
    const account = selectedAccounts.find(
      account => account.personalAccountId === accountId
    )
    if (!account) {
      return []
    }
    return [...account.permissions, ...account.autoPermissions]
  }

  return (
    <>
      <p className="font-bold text-24 xxs:text-20">Доступы</p>
      <div className="flex flex-col gap-20">
        {permissions?.map(project => {
          const isProjectSelected = project.personalAccounts.some(account =>
            isAccountSelected(account.id)
          )

          return (
            <div key={project.projectId}>
              <div className="flex gap-12">
                <AppCheckbox
                  onChange={() => onProjectToggle(project)}
                  checked={isProjectSelected}
                  isValid={!hasError}
                />
                <p className="font-semibold text-20 xxs:text-16">
                  {project.projectName}
                </p>
              </div>
              <hr className="my-8 text-grey-20" />
              <div className="flex flex-col gap-8">
                {project.personalAccounts.map(account => (
                  <PremisePicker
                    key={account.id}
                    personalAccount={account}
                    projectType={project.projectType}
                    projectName={project.projectName}
                    isChecked={isAccountSelected(account.id)}
                    isValid={!hasError}
                    selectedPermissions={getAccountPermissions(account.id)}
                    onSelect={selectedAccount =>
                      onAccountSelect(selectedAccount, project)
                    }
                    onPermissionsChange={onAccountPermissionsChange}
                  />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </>
  )
}
